import { useCallback, useMemo, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import {
  patientService,
  type CreatePatientInput,
} from "../services/patientService";
import type { MessagingPatient } from "../types";

type UsePatientLookupParams = {
  organizationId?: string;
};

interface UsePatientLookupResult {
  patients: MessagingPatient[];
  isSearching: boolean;
  hasSearchError: boolean;
  search: (params: { term?: string }) => Promise<void>;
  create: (params: {
    input: CreatePatientInput;
  }) => Promise<MessagingPatient | null>;
}

/**
 * usePatientLookup
 *
 * Hook de domínio para:
 * - buscar pacientes (simulado);
 * - criar paciente (simulado).
 *
 * Motivação:
 * - O Dialog de envio vive em `modules/*` e não pode depender de `applications/*`.
 * - Mantemos um contrato simples que pode ser substituído por service real depois.
 */
export function usePatientLookup(
  params: UsePatientLookupParams = {},
): UsePatientLookupResult {
  const [patients, setPatients] = useState<MessagingPatient[]>([]);
  const organizationId = params.organizationId;

  const {
    run: runSearch,
    loading: isSearching,
    error: searchError,
  } = useAsyncTask<[string?], MessagingPatient[]>({
    task: useCallback(
      async (term = "") => {
        const result = await patientService.search({ term, organizationId });
        setPatients(result);
        return result;
      },
      [organizationId],
    ),
  });

  const { run: runCreate } = useAsyncTask<
    [CreatePatientInput],
    MessagingPatient
  >({
    task: useCallback(async (input) => patientService.create(input), []),
  });

  const search = useCallback(
    async ({ term = "" }: { term?: string }) => {
      await runSearch(term);
    },
    [runSearch],
  );

  const create = useCallback(
    async ({ input }: { input: CreatePatientInput }) => {
      try {
        return await runCreate(input);
      } catch {
        return null;
      }
    },
    [runCreate],
  );

  const hasSearchError = useMemo(() => Boolean(searchError), [searchError]);

  return {
    patients,
    isSearching,
    hasSearchError,
    search,
    create,
  };
}
